// index.js
import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEdit,
  faSave,
  faTimes,
  faCopy,
  faChevronDown,
  faExchangeAlt,
  faSignOutAlt,
  faDownload,
  faFileInvoice
} from '@fortawesome/free-solid-svg-icons';

import { childApiService, dictionaryService, fileService, childTabDataService } from './actions/childApiService';
import { transformFrontendToBackend } from './actions/childApiService';
import { childrenApi } from '../../../../cabinet/components/CabinetChildrenCard/api/childrenApi';

// Импортируем компоненты
import BasicInfoBlock from './components/Blocks/BasicInfoBlock';
import BirthCertificateBlock from './components/Blocks/BirthCertificateBlock';
import HealthBlock from './components/Blocks/HealthBlock';
import BenefitsBlock from './components/Blocks/BenefitsBlock';
import ContactsBlock from './components/Blocks/ContactsBlock';
import HistoryTable from './components/Tables/HistoryTable';
import BillingTable from './components/Tables/BillingTable';
import AttendanceTable from './components/Tables/AttendanceTable';
import PaymentsTable from './components/Tables/PaymentsTable';
import AttendanceDetailsModal from './components/Modals/AttendanceDetailsModal';
import { localDateString } from '../../../../../components/helpers/datetranslate';

import { documentApi } from '../../../../adminDOU/components/ChildModalView/api/documentApi';

import './child-modal.css';
import './style.css';

// Основной компонент карточки ребенка
const ChildCardModal = ({ kg_id, gr_id, childId, isOpen, onClose, onSave, mode, tabelNumber, getFreeTab, typeKitchen }) => {
  const [formData, setFormData] = useState({});
  const [activeTab, setActiveTab] = useState('history');
  const [attendanceDetails, setAttendanceDetails] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [originalBackendData, setOriginalBackendData] = useState(null);
  const [benefitsList, setBenefitsList] = useState([]);
  const [specializationsList, setSpecializationsList] = useState([]);
  const [disabilitiesList, setDisabilitiesList] = useState([]);
  const [invoiceLoading, setInvoiceLoading] = useState(false);

  // Данные для табов
  const [historyData, setHistoryData] = useState([]);
  const [billingData, setBillingData] = useState([]);
  const [attendanceData, setAttendanceData] = useState([]);
  const [paymentsData, setPaymentsData] = useState([]);
  const [tabLoading, setTabLoading] = useState(false);

  // Определяем доступность редактирования
  const canEdit = mode !== 'viewonly';
  const isViewOnly = mode === 'viewonly';

  useEffect(() => {
    // Захист від гонки: відповіді для попередньої картки не повинні
    // потрапляти у стан, коли вже відкрито/закрито іншу.
    let cancelled = false;

    // Одразу скидаємо стан попередньої дитини, щоб ніколи не показувати
    // і не зберігати чужі дані під ідентифікатором нової картки.
    setFormData({});
    setOriginalBackendData(null);
    setIsEditing(false);
    setHistoryData([]);
    setBillingData([]);
    setAttendanceData([]);
    setPaymentsData([]);

    async function fetchData() {
      if (!childId || !isOpen) return;

      setLoading(true);
      try {
        // Загружаем справочники
        const [benefits, specializations, disabilities] = await Promise.all([
          dictionaryService.getBenefits(),
          dictionaryService.getSpecializations(),
          dictionaryService.getDisabilities(),
        ]);
        
        if (cancelled) return;
        setBenefitsList(benefits);
        setSpecializationsList(specializations);
        setDisabilitiesList(disabilities);

        // Загружаем данные ребенка
        const childData = await childApiService.getChild(childId);
        if (cancelled) return;
        // Сервер мав повернути саме цю дитину; інакше нічого не показуємо.
        if (childData.id && String(childData.id) !== String(childId)) {
          throw new Error(`Child id mismatch: requested ${childId}, got ${childData.id}`);
        }
        setFormData(Object.assign({}, childData, { tabelNumber, foodType: typeKitchen }));
        setOriginalBackendData(JSON.parse(JSON.stringify(childData)));

        // Загружаем данные для всех табов
        await loadAllTabData(childId, () => cancelled);
        if (cancelled) return;

        setIsEditing(false);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchData();

    return () => { cancelled = true; };
  }, [childId, isOpen, kg_id, gr_id]);

  // Функция загрузки данных для всех табов
  const loadAllTabData = async (childId, isCancelled = () => false) => {
    setTabLoading(true);
    try {
      const [history, attendance, billing, payments] = await Promise.all([
        loadHistoryData(childId),
        loadAttendanceData(childId),
        loadBillingData(childId),
        loadPaymentsData(childId)
      ]);

      if (isCancelled()) return;
      setHistoryData(history);
      setAttendanceData(attendance);
      setBillingData(billing);
      setPaymentsData(payments);
    } catch (error) {
      console.error('Error loading tab data:', error);
    } finally {
      if (!isCancelled()) setTabLoading(false);
    }
  };

  // Загрузка данных истории
  const loadHistoryData = async (childId) => {
    try {
      const result = await childTabDataService.getChildHistory(childId);
      if (result && result.length > 0) {
        return result;
      }
      
      if (formData.statuses && formData.statuses.length > 0) {
        return formData.statuses.map(status => ({
          ...status,
          kindergartenNumber: kg_id || status.kindergartenId,
          groupInfo: `Група ${gr_id || status.groupId}`,
          placeType: status.placeType || 'standart',
          kindergartenDistrict: status.district || 'Київ'
        }));
      }
      return [];
    } catch (error) {
      console.error('Error loading history data:', error);
      return [];
    }
  };

  // Загрузка данных начислений
  const loadBillingData = async (childId) => {
    try {
      if (kg_id) {
        const billingInfo = await childrenApi.getBillingInfo(childId, kg_id);
        return billingInfo || [];
      }
      return [];
    } catch (error) {
      console.error('Error loading billing data:', error);
      return [];
    }
  };

  // Загрузка данных посещений
  const loadAttendanceData = async (childId) => {
    try {
      const attendsInfo = await childrenApi.getAttendsInfo(childId);
      return attendsInfo || [];
    } catch (error) {
      console.error('Error loading attendance data:', error);
      return [];
    }
  };

  // Загрузка данных оплат
  const loadPaymentsData = async (childId) => {
    try {
      if (kg_id) {
        const paymentsInfo = await childrenApi.getPaymentsInfo(childId, kg_id);
        return paymentsInfo || [];
      }
      return [];
    } catch (error) {
      console.error('Error loading payments data:', error);
      return [];
    }
  };

  // Функция скачивания квитанции
  const handleDownloadInvoice = async () => {
    if (!childId || !kg_id) {
      alert('Немає даних для формування рахунку');
      return;
    }
    
    setInvoiceLoading(true);
    try {
      await childrenApi.downloadInvoice(childId, kg_id);
    } catch (error) {
      console.error('Error downloading invoice:', error);
      alert('Помилка при отриманні рахунку');
    } finally {
      setInvoiceLoading(false);
    }
  };

  // Обновление данных таба после сохранения
  const refreshTabData = async () => {
    await loadAllTabData(childId);
  };

  // Обработчик смены таба
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  // Обработчик просмотра деталей посещения
  const handleViewAttendanceDetails = (attendData) => {
    setAttendanceDetails(attendData);
  };

  const handleFieldChange = (block, field, value) => {
    if (isViewOnly) return;
    
    setFormData(prev => {
      const newData = { ...prev };
      
      switch (block) {
        case 'basic':
          if (field === 'firstName' || field === 'lastName' || field === 'middleName') {
            const firstName = field === 'firstName' ? value : prev.firstName;
            const lastName = field === 'lastName' ? value : prev.lastName;
            const middleName = field === 'middleName' ? value : prev.middleName;
            newData.fullName = `${lastName} ${firstName} ${middleName || ''}`.trim();
          }
          newData[field] = value;
          break;
          
        case 'certificate':
          newData[field] = value;
          break;
          
        case 'health':
          newData[field] = value;
          break;
          
        case 'benefits':
          newData[field] = value;
          if (field === 'benefitId') {
            const benefit = benefitsList.find(b => b._id === value);
            newData.benefitDescription = benefit?.name || '';
          }
          break;
          
        case 'contacts':
          if (field === 'relatedPerson') {
            const { index, field: personField, value: personValue } = value;
            newData.relatedPersons = newData.relatedPersons.map((person, i) => 
              i === index ? { ...person, [personField]: personValue } : person
            );
          } else if (field === 'relatedPersonRemove') {
            newData.relatedPersons = newData.relatedPersons.filter((_, i) => i !== value);
          } else if (field === 'addRelatedPerson') {
            newData.relatedPersons = [...(newData.relatedPersons || []), {
              lastName: '',
              firstName: '',
              middleName: '',
              taxNumber: '',
              phone: '',
              email: '',
              citizenship: '',
              relationshipDegree: ''
            }];
          } else {
            newData[field] = value;
          }
          break;
          
        case 'additional':
          newData[field] = value;
          break;
          
        default:
          newData[field] = value;
      }
      
      return newData;
    });
  };

  const handleSave = async () => {
    if (isViewOnly) return;

    // Остання лінія захисту: форма має належати саме тій дитині,
    // під чиїм childId іде PUT. Інакше перезапишемо чужу картку.
    if (!childId || !formData.id || String(formData.id) !== String(childId) || !originalBackendData) {
      console.error('Save blocked: form data does not belong to child', { childId, formId: formData.id });
      alert('Дані картки не відповідають обраній дитині. Закрийте картку та відкрийте її знову.');
      return;
    }

    setLoading(true);
    try {
      const backendData = transformFrontendToBackend(formData, originalBackendData);
      await childApiService.updateChild({ kg_id, gr_id, childId, data: backendData });
      
      await saveFilesForSection('health', formData.healthFiles, originalBackendData.healthFiles);
      await saveFilesForSection('benefits', formData.benefitFiles, originalBackendData.benefitFiles);
      await saveFilesForSection('certificate', formData.birthCertificateFiles, originalBackendData.birthCertificateFiles);
      
      await refreshTabData();
      
      if (onSave) {
        onSave(formData);
      }
      
      setIsEditing(false);
      alert('Дані успішно збережено');
    } catch (error) {
      console.error('Error saving child data:', error);
      // 4089/4090 — відмова сервера з поясненням українською
      alert(error && (error.code === 4089 || error.code === 4090)
        ? error.message
        : 'Помилка при збереженні даних');
    } finally {
      setLoading(false);
    }
  };

  const saveFilesForSection = async (section, newFiles, originalFiles) => {
    if (!newFiles || !Array.isArray(newFiles)) return;
    
    const originalFileIds = (originalFiles || []).map(f => f._id || f.file?._id).filter(Boolean);
    const newFileIds = newFiles.map(f => f._id || f.file?._id).filter(Boolean);

    for (const fileId of originalFileIds) {
      if (!newFileIds.includes(fileId)) {
        await fileService.deleteFile(childId, fileId, getPartForSection(section));
      }
    }
    
    for (const file of newFiles) {
      if (!file._id && !file.file?._id) {
        await fileService.uploadFile(childId, file, getPartForSection(section));
      }
    }
  };

  const getPartForSection = (section) => {
    switch (section) {
      case 'health': return 'specializations';
      case 'benefits': return 'benefits';
      case 'certificate': return 'cert';
      default: return 'documents';
    }
  };

  const getPathScanFields = (section) => {
    switch (section) {
      case 'health': return 'healthFiles';
      case 'benefits': return 'benefitFiles';
      case 'certificate': return 'birthCertificateFiles';
      default: return '';
    }
  };

  const handleFileView = async (file) => {
    try {
      if (file.file || file.content) {
        setFilePreview(file);
        return;
      }
      
      if (file._id || file.file?._id) {
        const fileId = file._id || file.file._id;
        const part = determineFilePart(file);
        
        setLoading(true);
        const fileBlob = await fileService.getFile(childId, fileId, part);
        
        const fileForPreview = {
          ...file,
          file: URL.createObjectURL(fileBlob),
          type: file.type || fileBlob.type
        };
        
        setFilePreview(fileForPreview);
      }
    } catch (error) {
      console.error('Error loading file for preview:', error);
      alert('Не вдалося завантажити файл для перегляду');
    } finally {
      setLoading(false);
    }
  };

  const determineFilePart = (file) => {
    if (file.section) {
      return getPartForSection(file.section);
    }
    
    if (file.name?.includes('medical') || file.name?.includes('health')) {
      return 'specializations';
    } else if (file.name?.includes('benefit')) {
      return 'benefits';
    } else if (file.name?.includes('birth') || file.name?.includes('certificate')) {
      return 'cert';
    }
    
    return 'documents';
  };

  const handleFileViewRequest = async (fileId, section) => {
    const part = getPartForSection(section);
    const file = formData[getPathScanFields(section)]?.find(f => f._id === fileId);
    if (!file || !file.type) return;
    return await fileService.getFile(childId, fileId, part, file.type);
  };

  const handleFileDownload = async (file, section) => {
    if (!file || !file._id) return;

    const { _id, name, type } = file;
    const part = getPartForSection(section);
    return await fileService.downloadFile(childId, _id, part, name, type);
  };

  const closeAttendanceDetails = () => {
    setAttendanceDetails(null);
  };

  const closeFilePreview = () => {
    setFilePreview(null);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const handleEditToggle = () => {
    if (isViewOnly) return;
    setIsEditing(!isEditing);
  };

  const handleCancelEdit = () => {
    if (isViewOnly) return;
    if (originalBackendData) {
      setFormData(JSON.parse(JSON.stringify(originalBackendData)));
    }
    setIsEditing(false);
  };

  const handleDownloadDocFiles = async (docId) => {
    if (!docId) return;
    try {
      const docInfo = await documentApi.getDocumentInfo(docId);
      if (docInfo.files) {
        for (let f of docInfo.files) {
          await documentApi.downloadDocumentFile(docId, f._id);
        }
      }
    } catch (error) {
      console.error('Error downloading document files:', error);
    }
  };

  // Рендер контента таба
  const renderTabContent = () => {
    if (tabLoading) {
      return (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Завантаження...</span>
          </div>
          <p className="mt-2">Завантаження даних...</p>
        </div>
      );
    }

    switch (activeTab) {
      case 'billing':
        return <BillingTable data={billingData} />;
      case 'attendance':
        return (
          <AttendanceTable 
            data={attendanceData} 
            onViewDetails={handleViewAttendanceDetails}
          />
        );
      case 'payments':
        return (
          <PaymentsTable 
            data={paymentsData}
            onDownloadInvoice={handleDownloadInvoice}
            invoiceLoading={invoiceLoading}
            childId={childId}
            kgId={kg_id}
          />
        );
      default:
        return <HistoryTable data={historyData} onDownload={handleDownloadDocFiles} />;
    }
  };

  if (!isOpen) return null;

  if (loading && !formData.id) {
    return (
      <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Завантаження...</span>
              </div>
              <p className="mt-3">Завантаження даних...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="modal fade child-card-modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog modal-dialog-centered modal-xl">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Картка дитини</h5>
              <div className="d-flex align-items-center gap-2">
                {canEdit && !isEditing && !isViewOnly && (
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={handleEditToggle}
                  >
                    <FontAwesomeIcon icon={faEdit} className="me-1" />
                    Редагувати
                  </button>
                )}
                {canEdit && isEditing && !isViewOnly && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={handleCancelEdit}
                  >
                    <FontAwesomeIcon icon={faTimes} className="me-1" />
                    Скасувати
                  </button>
                )}
                <button type="button" className="btn-close" onClick={onClose} />
              </div>
            </div>

            <div className="modal-body">
              {/* Profile Bar */}
              <div className="profile-bar">
                <div className="name">
                  {formData.fullName}
                  <span className="chip">Табельний номер {formData.fileNumber || formData.tabelNumber}</span>
                </div>
                <div className="meta">
                  <div className="meta-item">
                    <span className="label">Дата прийому:</span>
                    <span className="value">{localDateString(formData.admissionDate)}</span>
                  </div>
                  {false && <div className="meta-item">
                    <span className="label">ID:</span>
                    <span className="value">{formData.id}</span>
                    <FontAwesomeIcon 
                      icon={faCopy} 
                      style={{ cursor: 'pointer', marginLeft: '6px' }}
                      onClick={() => copyToClipboard(formData.id)}
                      title="Скопіювати ID"
                    />
                  </div>}
                </div>
              </div>

              {/* Basic Info Block */}
              <BasicInfoBlock
                data={formData}
                onChange={(field, value) => handleFieldChange('basic', field, value)}
                editable={isEditing}
                isViewOnly={isViewOnly}
                defaultOpen={true}
              />

              {/* Birth Certificate Block */}
              <BirthCertificateBlock
                data={formData}
                onChange={(field, value) => handleFieldChange('certificate', field, value)}
                editable={isEditing}
                isViewOnly={isViewOnly}
                defaultOpen={true}
                onFileViewRequest={(file_id, fileIndex) => handleFileViewRequest(file_id, "certificate")}
                onFileDownload={(file) => handleFileDownload(file, 'certificate')}
              />

              {/* Status Bar */}
              <div className={formData.residenceVerification ? "status-bar status-success" : "status-bar"}>
                <div>
                  {formData.residenceVerification ? 'Проживання в Києві підтверджене' : 'Проживання в Києві не підтверджене'}
                  <span className="date">{localDateString(formData.residenceVerificationDate)}</span>
                
                  <button className="btn btn-check-reg" title="Перевірити" onClick={() => dictionaryService.checkRegistrationKyivChild(childId)}>
                    Перевірити
                  </button>
                </div>
                <FontAwesomeIcon icon={faChevronDown} className="chevron" />
              </div>

              {/* Health Block */}
              <HealthBlock
                data={formData}
                onChange={(field, value) => handleFieldChange('health', field, value)}
                editable={isEditing}
                isViewOnly={isViewOnly}
                benefitsList={benefitsList}
                specializationsList={specializationsList}
                disabilitiesList={disabilitiesList}
                defaultOpen={true}
                onFileViewRequest={(file_id, fileIndex) => handleFileViewRequest(file_id, "health")}
                onFileDownload={(file) => handleFileDownload(file, 'health')}
              />

              {/* Benefit Block */}
              <BenefitsBlock
                data={formData}
                onChange={(field, value) => handleFieldChange('benefits', field, value)}
                editable={isEditing}
                isViewOnly={isViewOnly}
                benefitsList={benefitsList}
                defaultOpen={true}
                onFileViewRequest={(file_id, fileIndex) => handleFileViewRequest(file_id, "benefits")}
                onFileDownload={(file) => handleFileDownload(file, 'benefits')}
              />

              {/* Contacts Block */}
              <ContactsBlock
                data={formData}
                onChange={(field, value) => handleFieldChange('contacts', field, value)}
                editable={isEditing}
                isViewOnly={isViewOnly}
                defaultOpen={true}
              />

              {/* Additional Info */}
              <div className="additional-info mt-2">
                <div className="row gx-5 gy-3">
                  <div className="col-md-6">
                    <div className="kv-item">
                      <div className="kv-value">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={formData.isOrphan || false}
                            onChange={(e) => handleFieldChange('additional', 'isOrphan', e.target.checked)}
                            disabled={isViewOnly || !isEditing}
                            id="isOrphan"
                          />
                          <label className="form-check-label" htmlFor="isOrphan">
                            {formData.isOrphan ? 'Так' : 'Ні'}
                          </label>
                        </div>
                      </div>
                      <div className="kv-label">Діти-сироти</div>
                    </div>
                    <div className="kv-item">
                      <div className="kv-value">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={formData.isInvalidity || false}
                            onChange={(e) => handleFieldChange('additional', 'isInvalidity', e.target.checked)}
                            disabled={isViewOnly || !isEditing}
                            id="isInvalidity"
                          />
                          <label className="form-check-label" htmlFor="isInvalidity">
                            {formData.isInvalidity ? 'Так' : 'Ні'}
                          </label>
                        </div>
                      </div>
                      <div className="kv-label">Дитина з інвалідністю</div>
                    </div>
                    <div className="kv-item">
                      <div className="kv-value">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={formData.isDisplaced || false}
                            onChange={(e) => handleFieldChange('additional', 'isDisplaced', e.target.checked)}
                            disabled={isViewOnly || !isEditing}
                            id="isDisplaced"
                          />
                          <label className="form-check-label" htmlFor="isDisplaced">
                            {formData.isDisplaced ? 'Так' : 'Ні'}
                          </label>
                        </div>
                      </div>
                      <div className="kv-label">Переміщена особа</div>
                    </div>
                    <div className="kv-item">
                      <div className="kv-value">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={formData.isDistanceLearning || false}
                            onChange={(e) => handleFieldChange('additional', 'isDistanceLearning', e.target.checked)}
                            disabled={isViewOnly || !isEditing}
                            id="isDistanceLearning"
                          />
                          <label className="form-check-label" htmlFor="isDistanceLearning">
                            {formData.isDistanceLearning ? 'Так' : 'Ні'}
                          </label>
                        </div>
                      </div>
                      <div className="kv-label">Навчається дистанційно/онлайн</div>
                    </div>
                    <div className="kv-item">
                      <div className="kv-value">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={formData.isSpecEduc || false}
                            onChange={(e) => handleFieldChange('additional', 'isSpecEduc', e.target.checked)}
                            disabled={isViewOnly || !isEditing}
                            id="isSpecEduc"
                          />
                          <label className="form-check-label" htmlFor="isSpecEduc">
                            {formData.isSpecEduc ? 'Так' : 'Ні'}
                          </label>
                        </div>
                      </div>
                      <div className="kv-label">Є потреба в спеціальних умовах</div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="kv-item">
                      <div className="kv-value">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={formData.isContactLost || false}
                            onChange={(e) => handleFieldChange('additional', 'isContactLost', e.target.checked)}
                            disabled={isViewOnly || !isEditing}
                            id="isContactLost"
                          />
                          <label className="form-check-label" htmlFor="isContactLost">
                            {formData.isContactLost ? 'Так' : 'Ні'}
                          </label>
                        </div>
                      </div>
                      <div className="kv-label">Зв'язок з батьками або опікунами втрачений</div>
                    </div>
                    <div className="kv-item">
                      <div className="kv-label">Тип харчування</div>
                      <div className="kv-value d-flex align-items-center gap-2">
                        <select
                          className="form-select form-select-sm kv-select w-auto"
                          value={formData.foodType || 'Звичайне'}
                          onChange={(e) => handleFieldChange('additional', 'foodType', e.target.value)}
                          disabled={isViewOnly || !isEditing}
                        >
                          <option value="Звичайне">Звичайне</option>
                          <option value="Дієтичне">Дієтичне</option>
                          <option value="Безлактозне">Безлактозне</option>
                        </select>
                      </div>
                    </div>
                    <div className="kv-item">
                      <div className="kv-label">Табельний номер</div>
                      <div className="kv-value d-flex align-items-center gap-2">
                        <input
                          className="form-control form-control-sm"
                          style={{ width: '120px' }}
                          value={formData.tabelNumber || ''}
                          onChange={(e) => handleFieldChange('additional', 'tabelNumber', e.target.value)}
                          disabled={isViewOnly || !isEditing}
                          id="tabelNumber"
                        />
                        {/* Кнопка поряд із полем, лише коли табельний номер НЕ
                            заповнений (буває, що при зарахуванні номер не присвоєно).
                            getFreeTab() генерує випадковий 3-значний номер, унікальний
                            серед дітей цього садка. typeof-перевірка — захист, якщо
                            картку відкрито без цього prop. */}
                        {!isViewOnly && isEditing
                          && !(formData.tabelNumber && String(formData.tabelNumber).trim())
                          && typeof getFreeTab === 'function' && (
                          <button
                            type="button"
                            className="btn btn-check-reg btn-sm"
                            title="Згенерувати вільний табельний номер"
                            onClick={() => {
                              const f_n = getFreeTab();
                              if (f_n) handleFieldChange('additional', 'tabelNumber', f_n);
                            }}
                          >
                            Отримати номер
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Табы с подключенными данными */}
              <div className="tabs-actions">
                <ul className="nav nav-tabs small" role="tablist">
                  <li className="nav-item" role="presentation">
                    <button
                      className={`nav-link ${activeTab === 'history' ? 'active' : ''}`}
                      onClick={() => handleTabChange('history')}
                    >
                      Історія
                    </button>
                  </li>
                  <li className="nav-item" role="presentation">
                    <button
                      className={`nav-link ${activeTab === 'billing' ? 'active' : ''}`}
                      onClick={() => handleTabChange('billing')}
                    >
                      Нарахування
                    </button>
                  </li>
                  <li className="nav-item" role="presentation">
                    <button
                      className={`nav-link ${activeTab === 'attendance' ? 'active' : ''}`}
                      onClick={() => handleTabChange('attendance')}
                    >
                      Відвідування
                    </button>
                  </li>
                  <li className="nav-item" role="presentation">
                    <button
                      className={`nav-link ${activeTab === 'payments' ? 'active' : ''}`}
                      onClick={() => handleTabChange('payments')}
                    >
                      Оплати
                    </button>
                  </li>
                </ul>
                {false && canEdit && !isViewOnly && (
                  <div className="right-links">
                    <a href="#">
                      <FontAwesomeIcon icon={faSignOutAlt} className="me-1" />
                      Відрахувати
                    </a>
                    <a href="#">
                      <FontAwesomeIcon icon={faExchangeAlt} className="me-1" />
                      Перевести
                    </a>
                  </div>
                )}
              </div>

              <div className="tab-content pt-3">
                {renderTabContent()}
              </div>
            </div>

            <div className="modal-footer justify-content-end">
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                Закрити
              </button>
              {isEditing && !isViewOnly && (
                <button 
                  className="btn btn-primary"
                  onClick={handleSave}
                  disabled={loading}
                >
                  <FontAwesomeIcon icon={faSave} className="me-1" />
                  {loading ? 'Збереження...' : 'Зберегти'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Модальное окно детального просмотра посещений */}
      <AttendanceDetailsModal
        data={attendanceDetails}
        isOpen={!!attendanceDetails}
        onClose={closeAttendanceDetails}
      />

      {/* Модальное окно предпросмотра файла */}
      {filePreview && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Перегляд файлу: {filePreview.name}</h5>
                <button type="button" className="btn-close" onClick={closeFilePreview} />
              </div>
              <div className="modal-body">
                {filePreview.type?.includes('image') ? (
                  <img
                    src={filePreview.file?.storage ? 
                      `${process.env.REACT_APP_API_URL}/files/preview/${filePreview.file.storage.name}` :
                      filePreview.file
                    }
                    alt="Preview"
                    className="img-fluid w-100"
                  />
                ) : filePreview.type?.includes('pdf') ? (
                  <iframe
                    src={filePreview.file?.storage ?
                      `${process.env.REACT_APP_API_URL}/files/preview/${filePreview.file.storage.name}` :
                      filePreview.file
                    }
                    className="w-100"
                    style={{ height: '500px' }}
                    title="PDF Preview"
                  />
                ) : (
                  <div className="text-center py-5">
                    <p>Попередній перегляд недоступний для цього типу файлу</p>
                    <button
                      className="btn btn-primary"
                      onClick={() => handleFileDownload(filePreview, 'documents')}
                    >
                      <FontAwesomeIcon icon={faDownload} className="me-2" />
                      Завантажити файл
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChildCardModal;